import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "wouter";
import {
  useListMyTickets,
  useCreateSupportTicket,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useFileUpload } from "@/hooks/use-file-upload";
import { formatDate } from "@/lib/format";
import { Plus, Paperclip, X, TicketCheck, ChevronRight, AlertCircle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

const ticketSchema = z.object({
  type: z.enum(["bug", "feature_request", "other"]),
  title: z.string().min(1, "Title is required").max(200, "Title is too long"),
  description: z
    .string()
    .min(1, "Description is required")
    .max(5000, "Description is too long"),
  priority: z.enum(["low", "medium", "high", "urgent"]),
});

type TicketFormData = z.infer<typeof ticketSchema>;

const typeLabels: Record<string, string> = {
  bug: "Bug Report",
  feature_request: "Feature Request",
  other: "Other",
};

const priorityLabels: Record<string, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
};

const priorityColors: Record<string, string> = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-blue-100 text-blue-700",
  high: "bg-orange-100 text-orange-700",
  urgent: "bg-red-100 text-red-700",
};

const statusColors: Record<string, string> = {
  open: "bg-green-100 text-green-700",
  in_progress: "bg-blue-100 text-blue-700",
  resolved: "bg-slate-100 text-slate-600",
  closed: "bg-slate-100 text-slate-500",
};

const statusLabels: Record<string, string> = {
  open: "Open",
  in_progress: "In Progress",
  resolved: "Resolved",
  closed: "Closed",
};

export default function SupportPage() {
  const [showForm, setShowForm] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [newTicketId, setNewTicketId] = useState<string | null>(null);
  const [attachment, setAttachment] = useState<{
    objectPath: string;
    fileName: string;
    contentType: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data: tickets, isLoading } = useListMyTickets();
  const createTicket = useCreateSupportTicket();
  const { uploadFile, isUploading, error: uploadError, setError: setUploadError } = useFileUpload({
    onSuccess: (result) => {
      setAttachment(result);
      toast.success("File attached successfully");
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<TicketFormData>({
    resolver: zodResolver(ticketSchema),
    defaultValues: { priority: "medium", type: "bug" },
  });

  const selectedType = watch("type");
  const selectedPriority = watch("priority");

  const onSubmit = handleSubmit(async (data) => {
    createTicket.mutate(
      {
        data: {
          ...data,
          attachmentPath: attachment?.objectPath,
          attachmentName: attachment?.fileName,
          attachmentContentType: attachment?.contentType,
        },
      },
      {
        onSuccess: (result) => {
          reset();
          setAttachment(null);
          setShowForm(false);
          queryClient.invalidateQueries({ queryKey: ["listMyTickets"] });
          setNewTicketId(result?.id ?? null);
          setShowConfirmation(true);
        },
        onError: () => {
          toast.error("Failed to submit ticket");
        },
      },
    );
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    await uploadFile(file);
    e.target.value = "";
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Support</h1>
          <p className="text-muted-foreground">Submit tickets and track your requests.</p>
        </div>
        <Button onClick={() => setShowForm((v) => !v)}>
          <Plus className="mr-2 h-4 w-4" />
          {showForm ? "Cancel" : "New Ticket"}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>New Support Ticket</CardTitle>
            <CardDescription>
              Describe your issue, feature request, or question.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="type">Type</Label>
                  <Select
                    value={selectedType}
                    onValueChange={(v) => setValue("type", v as TicketFormData["type"])}
                  >
                    <SelectTrigger id="type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bug">Bug Report</SelectItem>
                      <SelectItem value="feature_request">Feature Request</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.type && (
                    <p className="text-sm text-destructive">{errors.type.message}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="priority">Priority</Label>
                  <Select
                    value={selectedPriority}
                    onValueChange={(v) => setValue("priority", v as TicketFormData["priority"])}
                  >
                    <SelectTrigger id="priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.priority && (
                    <p className="text-sm text-destructive">{errors.priority.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  placeholder="Brief summary of your issue..."
                  {...register("title")}
                  maxLength={200}
                />
                {errors.title && (
                  <p className="text-sm text-destructive">{errors.title.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Please describe your issue in detail..."
                  rows={5}
                  {...register("description")}
                  maxLength={5000}
                />
                {errors.description && (
                  <p className="text-sm text-destructive">{errors.description.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>Attachment (optional)</Label>
                {attachment ? (
                  <div className="flex items-center gap-2 p-2.5 border rounded-md bg-muted/50">
                    <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-sm truncate flex-1">{attachment.fileName}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={() => setAttachment(null)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,text/plain,.doc,.docx,.xls,.xlsx"
                      onChange={handleFileChange}
                      disabled={isUploading}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      <Paperclip className="mr-2 h-4 w-4" />
                      {isUploading ? "Uploading..." : "Attach file"}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-1">
                      Images, PDF, or Office documents up to 10 MB
                    </p>
                    {uploadError && (
                      <p className="text-sm text-destructive mt-1 flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5" />
                        {uploadError}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    reset();
                    setAttachment(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createTicket.isPending || isUploading}
                >
                  {createTicket.isPending ? "Submitting..." : "Submit Ticket"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ticket Submitted</DialogTitle>
            <DialogDescription>
              Your support ticket has been submitted successfully. You can find it and track its progress under your list of tickets below.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setShowConfirmation(false)}>
              OK
            </Button>
            {newTicketId && (
              <Button asChild onClick={() => setShowConfirmation(false)}>
                <Link href={`/support/${newTicketId}`}>View Ticket</Link>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div>
        <h2 className="text-xl font-semibold mb-4">My Tickets</h2>
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-xl" />
            ))}
          </div>
        ) : !tickets || tickets.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <TicketCheck className="h-12 w-12 text-muted-foreground/40 mb-3" />
              <p className="text-muted-foreground">No tickets yet</p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                Click "New Ticket" to submit your first support request.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <Link key={ticket.id} href={`/support/${ticket.id}`}>
                <Card className="cursor-pointer hover:border-primary/40 transition-colors">
                  <CardContent className="py-4 px-4 sm:px-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex items-start gap-2 flex-wrap">
                          <span className="font-medium break-words min-w-0">{ticket.title}</span>
                          <Badge
                            variant="secondary"
                            className={`text-xs shrink-0 ${priorityColors[ticket.priority] ?? ""}`}
                          >
                            {priorityLabels[ticket.priority]}
                          </Badge>
                          <Badge
                            variant="secondary"
                            className={`text-xs shrink-0 ${statusColors[ticket.status] ?? ""}`}
                          >
                            {statusLabels[ticket.status]}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground break-words">
                          {typeLabels[ticket.type]} · {formatDate(ticket.createdAt)}
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
